import { initializePaddle, type Paddle } from '@paddle/paddle-js';

export type PaddleEnvironment = 'sandbox' | 'production';

export function getPaddleClientToken(): string | undefined {
  const token = process.env.REACT_APP_PADDLE_CLIENT_TOKEN?.trim();
  return token || undefined;
}

/** Token prefix is the source of truth (live_ → production, test_ → sandbox). */
export function resolvePaddleEnvironment(token: string): PaddleEnvironment {
  if (token.startsWith('live_')) {
    return 'production';
  }
  if (token.startsWith('test_')) {
    return 'sandbox';
  }

  return process.env.REACT_APP_PADDLE_ENV === 'production'
    ? 'production'
    : 'sandbox';
}

let paddlePromise: Promise<Paddle | undefined> | undefined;

export function getPaddleInstance(): Promise<Paddle | undefined> {
  if (!paddlePromise) {
    const token = getPaddleClientToken();

    if (!token) {
      return Promise.resolve(undefined);
    }

    const environment = resolvePaddleEnvironment(token);

    if (
      process.env.NODE_ENV === 'development' &&
      process.env.REACT_APP_PADDLE_ENV === 'sandbox' &&
      token.startsWith('live_')
    ) {
      console.warn(
        '[Paddle] Using a live_ token while REACT_APP_PADDLE_ENV=sandbox. ' +
        'Environment is set to production from the token. Use a test_ token for sandbox, ' +
        'or production price IDs for live checkout.',
      );
    }

    paddlePromise = initializePaddle({
      token,
      environment,
    });
  }

  return paddlePromise;
}

export async function openPaddleCheckout(
  priceId: string,
  isCancelled?: () => boolean,
): Promise<void> {
  if (!priceId) {
    console.error(
      '[Paddle] Missing price ID for this plan. Add production price IDs in src/constants/urls.ts.',
    );
    return;
  }

  const paddle = await getPaddleInstance();
  if (!paddle) {
    console.error('[Paddle] Not initialized. Set REACT_APP_PADDLE_CLIENT_TOKEN.');
    return;
  }

  if (isCancelled?.()) {
    return;
  }

  const frameTarget = 'checkout-container';
  const container = document.querySelector(`.${frameTarget}`);
  if (!container) {
    console.error(
      `[Paddle] Inline checkout requires an element with class "${frameTarget}".`,
    );
    return;
  }

  paddle.Checkout.open({
    items: [{ priceId, quantity: 1 }],
    settings: {
      successUrl: `${window.location.origin}/success`,
      displayMode: 'inline',
      frameTarget,
      frameInitialHeight: 400,
      frameStyle:
        'width: 100%; min-width: 0; background-color: transparent; border: none;',
    },
  });
}
